import { supabase } from '../config/supabaseClient.js';

/**
 * ESTOQUE: situação atual das chaves (não somar com fluxo).
 * @returns {Promise<{disponiveis:number, retiradas:number, total:number}>}
 */
export async function estoqueChaves() {
  const base = supabase.from('chave').select('*', { count: 'exact', head: true }).eq('status', 'ATIVO');
  const [{ count: total }, { count: disponiveis }, { count: retiradas }] = await Promise.all([
    base,
    supabase.from('chave').select('*', { count: 'exact', head: true }).eq('status', 'ATIVO').eq('situacao', 'DISPONIVEL'),
    supabase.from('chave').select('*', { count: 'exact', head: true }).eq('status', 'ATIVO').eq('situacao', 'RETIRADA'),
  ]);
  return { disponiveis: disponiveis || 0, retiradas: retiradas || 0, total: total || 0 };
}

/**
 * FLUXO: movimentações em um período (âncora = data_hora_movimentacao).
 * @returns {Promise<{retiradas:number, devolucoes:number}>}
 */
export async function fluxoPeriodo(deISO, ateISO) {
  const consulta = (tipo) =>
    supabase.from('movimentacao_chaves').select('*', { count: 'exact', head: true })
      .eq('tipo_movimentacao', tipo)
      .gte('data_hora_movimentacao', deISO)
      .lte('data_hora_movimentacao', ateISO);
  const [{ count: retiradas }, { count: devolucoes }] = await Promise.all([consulta('RETIRADA'), consulta('DEVOLUCAO')]);
  return { retiradas: retiradas || 0, devolucoes: devolucoes || 0 };
}

/**
 * MÉTRICAS DO DASHBOARD (velocímetro), calculadas no banco (RPC dashboard_metricas).
 * @returns {Promise<null|{entregues_total:number, entregues_cadastrados:number, devolucoes_total:number, devolucoes_mesmo_dia:number}>}
 */
export async function metricasDashboard(deISO, ateISO) {
  const { data, error } = await supabase.rpc('dashboard_metricas', { p_de: deISO, p_ate: ateISO });
  if (error) { console.error('[metricasDashboard]', error.message); return null; }
  return {
    entregues_total: Number(data?.entregues_total || 0),
    entregues_cadastrados: Number(data?.entregues_cadastrados || 0),
    devolucoes_total: Number(data?.devolucoes_total || 0),
    devolucoes_mesmo_dia: Number(data?.devolucoes_mesmo_dia || 0),
  };
}

/**
 * Métricas do KPI de Crachá (reincidência mensal: mesmo colaborador esquece 2+ vezes no mês), via RPC.
 * @returns {Promise<null|{reincid_total:number, reincid_repetentes:number}>}
 */
export async function metricasCracha(deISO, ateISO) {
  const { data, error } = await supabase.rpc('cracha_metricas', { p_de: deISO, p_ate: ateISO });
  if (error) { console.error('[metricasCracha]', error.message); return null; }
  return {
    reincid_total: Number(data?.reincid_total || 0),
    reincid_repetentes: Number(data?.reincid_repetentes || 0),
  };
}

/** Lê as metas (percentuais de atendimento) de configuracoes. */
export async function lerMetas() {
  const { data } = await supabase.from('configuracoes').select('chave, valor')
    .in('chave', ['meta_entregues_cadastrados', 'meta_devolucoes_mesmo_dia', 'meta_cracha_reincidencia']);
  const m = {};
  (data || []).forEach((r) => { m[r.chave] = Number(r.valor); });
  return {
    entregues_cadastrados: Number.isFinite(m.meta_entregues_cadastrados) ? m.meta_entregues_cadastrados : 90,
    devolucoes_mesmo_dia: Number.isFinite(m.meta_devolucoes_mesmo_dia) ? m.meta_devolucoes_mesmo_dia : 90,
    cracha_reincidencia: Number.isFinite(m.meta_cracha_reincidencia) ? m.meta_cracha_reincidencia : 10,
  };
}

/** Grava uma meta (somente GESTOR, garantido por RLS). @returns {Promise<boolean>} */
export async function salvarMeta(chave, valor) {
  const { error } = await supabase.from('configuracoes')
    .upsert({ chave, valor, updated_at: new Date().toISOString() }, { onConflict: 'chave' });
  if (error) console.error('[salvarMeta]', error.message);
  return !error;
}

/** Intervalo ISO de hoje (fuso local). */
export function hojeIntervalo() {
  const a = new Date();
  return {
    de: new Date(a.getFullYear(), a.getMonth(), a.getDate(), 0, 0, 0).toISOString(),
    ate: new Date(a.getFullYear(), a.getMonth(), a.getDate(), 23, 59, 59).toISOString(),
  };
}

/** Intervalo ISO por período: 'u12m' (últimos 12 meses) ou um ano 'AAAA' (fuso local). */
export function intervaloPeriodo(periodo) {
  const a = new Date();
  let ini, fim;
  if (/^\d{4}$/.test(String(periodo))) {
    const y = parseInt(periodo, 10);
    ini = new Date(y, 0, 1, 0, 0, 0);
    fim = new Date(y, 11, 31, 23, 59, 59);
  } else {
    // últimos 12 meses (inclui o mês atual)
    fim = new Date(a.getFullYear(), a.getMonth(), a.getDate(), 23, 59, 59);
    ini = new Date(a.getFullYear(), a.getMonth() - 11, 1, 0, 0, 0);
  }
  return { de: ini.toISOString(), ate: fim.toISOString() };
}

/* ===================== Relatorios salvos (snapshot) ===================== */

/** Gera os dados do relatorio "Producao da recepcao" via RPC (nao salva). */
export async function gerarProducaoRecepcao(deISO, ateISO) {
  const { data, error } = await supabase.rpc('relatorio_producao_recepcao', { p_de: deISO, p_ate: ateISO });
  if (error) { console.error('[relatorio]', error.message); return null; }
  return data;
}

/** Gera os dados do relatorio de Visitas administrativas via RPC. */
export async function gerarVisitas(deISO, ateISO) {
  const { data, error } = await supabase.rpc('relatorio_visitas', { p_de: deISO, p_ate: ateISO });
  if (error) { console.error('[relatorio visitas]', error.message); return null; }
  return data;
}


/** Salva um relatorio gerado (snapshot). @returns {{id?:string, erro?:string}} */
export async function salvarRelatorio(r) {
  const { data, error } = await supabase.from('relatorio').insert(r).select('id').single();
  return { id: data?.id, erro: error ? error.message : null };
}

export async function listarRelatorios() {
  const { data, error } = await supabase.from('relatorio')
    .select('id, tipo, titulo, periodo_de, periodo_ate, gerado_por_nome, created_at')
    .order('created_at', { ascending: false }).limit(200);
  if (error) { console.error('[relatorios]', error.message); return []; }
  return data || [];
}

export async function carregarRelatorio(id) {
  const { data } = await supabase.from('relatorio').select('*').eq('id', id).maybeSingle();
  return data;
}

export async function apagarRelatorio(id) {
  const { data, error } = await supabase.from('relatorio').delete().eq('id', id).select('id');
  return !error && Array.isArray(data) && data.length > 0;
}
